import axios from 'axios';

import { handleForbiddenResource, isDeadSessionRejection } from '~/common/authUtils';
import sessionStore from '~/components2/TokenValidator/store';
import getConfig from '~/config';

const config = getConfig();

const instance = axios.create({
  baseURL: config.apiServerBase,
  withCredentials: true,
});

/** Matches the session-validity endpoint on path alone, so a query value cannot spoof it. */
function isValidateTokenUrl(url: unknown): boolean {
  if (typeof url !== 'string') {
    return false;
  }
  return url.split('?')[0].split('#')[0].endsWith('/v1/auth/validateToken');
}

/** Response-error interceptor shared by every axios instance: 902 redirect, 401/403 session handling, then rejects. */
export async function handleApiResponseError(e: unknown) {
  if (axios.isAxiosError(e)) {
    const status = e.response?.status;
    if (e.response?.status === 902) {
      window.location = e.response.data;
    } else if (isDeadSessionRejection(e)) {
      const isValidateToken = isValidateTokenUrl(e.response?.request?.responseURL);

      // validateToken also answers 403 for a dead session; every other 403 is a per-resource denial.
      if (isValidateToken || status === 401) {
        sessionStore.expire();
      } else if (e.response?.data?.allowAdminToJoinOrg === true) {
        const result = confirm(
          '@CivilGrid Admin: You do not currently have access to this organization, would you like to join as an admin?',
        );

        if (result === true) {
          const d = e.response?.data;
          const url = `/admin/join-org-as-admin?organizationId=${
            d.organizationId
          }&redirectUri=${encodeURIComponent(window.location.pathname)}`;
          window.location.assign(url);
        } else {
          handleForbiddenResource();
        }
      } else {
        handleForbiddenResource();
      }
    }
  }
  return Promise.reject(e);
}

instance.interceptors.response.use((response) => response, handleApiResponseError);

export function getAxiosClient() {
  return instance;
}

export function setToken(token: string) {
  window.CG_TOKEN = token;
  if (token) {
    instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    // Empty token = cookie session or logged out. Delete the header rather than send an empty
    // `Bearer `: cookie-mode requests authenticate via the HttpOnly cookie (withCredentials), and
    // a stray empty Bearer header can break that. TODO(INFRA-218): the only behavior once rolled out.
    delete instance.defaults.headers.common['Authorization'];
  }
}

export function sortByName(objs: any[]) {
  const _objs = [...objs];
  return _objs.sort((a, b) => {
    return a?.name?.trim().localeCompare(b?.name?.trim());
  });
}
